import {
  AppBar,
  Button,
  Container,
  Divider,
  Drawer,
  Hidden,
  IconButton,
  Toolbar,
  Typography
} from "@material-ui/core";
import CssBaseline from "@material-ui/core/CssBaseline";
import { makeStyles } from "@material-ui/core/styles";
import { ChevronLeft as ChevronLeftIcon, Menu as MenuIcon } from "@material-ui/icons";
import cx from "classnames";
import moment from "moment";
import React, { useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Link as RouterLink, matchPath, useLocation } from "react-router-dom";
import { GetAvailableUserLinks } from "../features/lib";
import { useCongress } from "../hooks";
import ChatWidget from "./ChatWidget";
import Flaticon from "./Flaticon";
import PoliciesViewer from "./PoliciesViewer";
import SideBar from "./SideBar";
import UserMenu from "./UserMenu";
import { ReactComponent as MyBadgeSvg } from "../assets/icons/my badge.svg";

const useStyles = makeStyles(theme => {
  return {
    root: {
      height: "100vh"
    },
    appBar: {
      marginBottom: 0
    },
    toolbar: {
      height: theme.spacing(11)
    },
    logo: {
      height: theme.spacing(7),
      width: theme.spacing(7),
      borderRadius: theme.spacing(1),
      margin: theme.spacing(2)
    },
    header: {
      flexGrow: 1,
      display: "flex",
      alignItems: "center",
      height: 90
    },
    drawerContent: {
      paddingLeft: theme.spacing(2),
      height: "100%"
    },
    drawerBar: {
      padding: theme.spacing(1),
      display: "flex",
      justifyContent: "flex-end"
    },
    menu: {
      flexGrow: 1,
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      "& > *": {
        marginLeft: theme.spacing(1)
      }
    },
    main: {
      display: "flex",
      height: "calc(100% - 90px)"
    },
    content: {
      height: "100%",
      overflowX: "hidden",
      overflowY: "auto",
      flexGrow: 1,
      paddingTop: theme.spacing(3)
    },
    footerBarSpace: {
      flexGrow: 1
    },
    icon: {
      width: theme.spacing(3),
      height: theme.spacing(3),
      fill: theme.overrides.MuiAppBar.colorDefault.color
    }
  };
});

const BaseLayout = ({ children, className }) => {
  const intl = useIntl();

  const { pathname } = useLocation();

  const { congress } = useCongress();
  const classes = useStyles(congress);

  const [menuOpen, setMenuOpen] = useState(false);

  const policiesViewerRef = useRef({ current: {} });

  const extraLinks = [];

  if (congress.enableFeatureMyBadge) {
    extraLinks.push({
      label: intl.formatMessage({
        id: "Shared.BaseLayout.MyBadge",
        defaultMessage: "My Badge"
      }),
      icon: MyBadgeSvg,
      route: "/my-badge",
      availableUnpublished: false
    });
  }

  const userLinks = GetAvailableUserLinks(extraLinks);

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBar position="sticky" color="default" className={classes.appBar} elevation={0}>
        <Container maxWidth="xl" disableGutters>
          <Toolbar className={classes.toolbar}>
            <div className={classes.header}>
              {!matchPath(pathname, "/home") && (
                <Hidden mdUp>
                  <IconButton onClick={() => setMenuOpen(true)} edge="start">
                    <MenuIcon />
                  </IconButton>
                </Hidden>
              )}
              <Hidden smDown>
                <RouterLink to={congress.open ? "/home" : "/profile"}>
                  <img src={congress.logo} alt={congress.name} className={classes.logo} />
                </RouterLink>
              </Hidden>
              <div>
                <Typography variant="h5">
                  <strong>{congress.name}</strong>
                </Typography>

                {!congress.enableFeatureLearn && (
                  <Typography variant="subtitle2">
                    {moment(congress.startAt).format("DD/MM/YYYY")}
                    &nbsp;-&nbsp;
                    {moment(congress.endAt).format("DD/MM/YYYY")}
                  </Typography>
                )}
              </div>
            </div>
            <div className={classes.menu}>
              <Hidden smDown>
                {userLinks.map(({ icon: Icon, label, route }) => (
                  <Button variant="text" component={RouterLink} to={route}>
                    {typeof Icon === "string" ? (
                      <Flaticon icon={Icon} />
                    ) : (
                      <Icon className={classes.icon} />
                    )}
                    &nbsp;&nbsp;{label}
                  </Button>
                ))}
              </Hidden>
              <UserMenu />
            </div>
          </Toolbar>
        </Container>
      </AppBar>

      <Container component="main" className={classes.main} maxWidth="xl">
        {!matchPath(pathname, "/home") && (
          <>
            <Hidden mdUp>
              <Drawer
                variant="temporary"
                anchor="left"
                onClose={() => setMenuOpen(false)}
                open={menuOpen}
                className={classes.drawer}
              >
                <div className={classes.drawerBar}>
                  <IconButton onClick={() => setMenuOpen(false)}>
                    <ChevronLeftIcon />
                  </IconButton>
                </div>
                <Divider />
                <div className={classes.drawerContent}>
                  <SideBar />
                </div>
              </Drawer>
            </Hidden>
            <Hidden smDown>
              <SideBar />
            </Hidden>
          </>
        )}
        <div id="scrollerContent" className={cx(classes.content, className)}>
          {children}

          {matchPath(pathname, "/home") && (
            <>
              <Toolbar>
                <Button variant="text" color="primary" component={RouterLink} to="/help-desk">
                  <Flaticon icon="lifesaver" fontSize="1.25rem" />
                  <Hidden xsDown>
                    &nbsp;&nbsp;
                    {intl.formatMessage({
                      id: "Shared.SideBar.HelpDesk",
                      defaultMessage: "Help desk"
                    })}
                  </Hidden>
                </Button>
                <Button variant="text" color="primary" component={RouterLink} to="/info">
                  <Flaticon icon="question" fontSize="1.25rem" />
                  <Hidden xsDown>
                    &nbsp;&nbsp;
                    {congress.infoPageTitle ||
                      intl.formatMessage({
                        id: "Shared.SideBar.Info",
                        defaultMessage: "Info"
                      })}
                  </Hidden>
                </Button>
                <Typography className={classes.footerBarSpace} />
                <Button
                  color="primary"
                  onClick={() => {
                    policiesViewerRef.current.setOpenPrivacyPolicy(true);
                  }}
                >
                  Privacy Policy
                </Button>
                <Button
                  color="primary"
                  onClick={() => {
                    policiesViewerRef.current.setOpenCookiePolicy(true);
                  }}
                >
                  Cookie Policy
                </Button>

                <PoliciesViewer policiesViewerRef={policiesViewerRef} />
              </Toolbar>
            </>
          )}
        </div>
      </Container>

      <ChatWidget />
    </div>
  );
};

export default BaseLayout;
